import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { Task } from '../../lib/tasksApi';
import { formatEstimate } from '../../lib/estimate';
import { shortIdentifier } from '../../lib/taskIdentifier';
import {
  AssigneeEditor,
  DueDateEditor,
  PriorityEditor,
  StatusEditor,
} from './linear/inlineEditors';
import { EstimateEditor } from './linear/DetailSidebar';
import { PRIORITY_COLOR, PRIORITY_LABEL } from './linear/taskVisuals';
import { CopyTaskLinkButton } from './CopyTaskLinkButton';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Rich hover preview for a task chip — a miniature of the task detail page
 * (identifier + project, title, description, properties) shown on hover.
 *
 * Since 2026-09-29 (Andrei) the properties are the same inline editors as the
 * detail page: status, priority, assignee, due date and estimate change from
 * the card, without opening the task. While one of their dropdowns is open
 * the card is pinned — its close timer and its scroll-to-close are off — so
 * moving the pointer into the dropdown (a separate portal) does not dismiss
 * the card underneath it.
 *
 * The calendar chips only have room for a title, so everything else used to be
 * crammed into a native `title` attribute: an unstyled, OS-rendered stack of
 * lines that arrives after a ~1s delay and cannot show a status glyph, an
 * avatar or a label colour. This renders the same information as the detail
 * page does, so scanning a month of work no longer means opening tasks one by
 * one.
 *
 * Dependency-free like the rest of the app's overlays: portal + a measured
 * position, no floating-ui/Radix.
 */

const CARD_WIDTH = 320;
// Long enough that dragging the pointer across a full calendar row does not
// flash a card per cell, short enough to feel like a hover and not a click.
const OPEN_DELAY_MS = 260;
// Grace period so the pointer can cross the gap into the card itself.
const CLOSE_DELAY_MS = 120;
const GAP = 8;

/**
 * Markdown description → one line of plain text.
 *
 * The preview is a teaser, not a document: rendering real markdown in a 320px
 * card would reintroduce headings, code blocks and images at sizes that make
 * the card taller than the thing it previews. Stripping the syntax keeps the
 * card a fixed, scannable shape.
 */
function plainTextPreview(markdown: string, maxChars = 220): string {
  const text = markdown
    .replace(/```[\s\S]*?```/g, ' ') // fenced code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links → their text
    .replace(/^\s{0,3}#{1,6}\s+/gm, '') // headings
    .replace(/^\s{0,3}>\s?/gm, '') // blockquotes
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, '• ') // list markers
    .replace(/[*_`]/g, '') // emphasis / inline code
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > maxChars ? `${text.slice(0, maxChars).trimEnd()}…` : text;
}

/**
 * Lets content inside a HoverCard hold it open (an editor's dropdown is open).
 * Counts, so several editors can nest their open/close without fighting.
 */
const HoverCardPinContext = createContext<(pinned: boolean) => void>(() => {});

/** True while a calendar chip is being dragged: no card opens, an open one closes. */
export const HoverCardSuppressedContext = createContext(false);

/** One "label: value" line of a preview card — shared by every hover card. */
export function PreviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[86px] shrink-0 text-[11.5px] text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[12.5px] text-gray-700">
        {children}
      </div>
    </div>
  );
}

function TaskPreviewCard({ task }: { task: Task }) {
  const { t } = useI18n();
  const pin = useContext(HoverCardPinContext);
  const description = task.description ? plainTextPreview(task.description) : '';

  return (
    <div className="p-3.5">
      <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500">
        {task.projectName && <span className="min-w-0 truncate">{task.projectName}</span>}
        {task.projectName && task.identifier && <span className="shrink-0">›</span>}
        {task.identifier && (
          // Shortened like the list rows: a long project key would otherwise
          // push the whole line out of the 320px card.
          <span title={task.identifier} className="min-w-0 truncate font-mono text-gray-600">
            {shortIdentifier(task.identifier)}
          </span>
        )}
        {/* Copy the task's link from here, without opening it (Andrei,
            2026-10-01): the card is the place people look at a task from the
            calendar, so it is where they want to grab its link from. */}
        <CopyTaskLinkButton
          task={task}
          size={14}
          className="-my-1 ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-800"
        />
      </div>

      <p className="mt-1 line-clamp-2 text-[14px] font-semibold leading-snug text-gray-900">
        {task.title}
      </p>

      {description && (
        <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed text-gray-500">
          {description}
        </p>
      )}

      {/* Editable, like the detail page's sidebar: each editor is its own
          trigger + dropdown and saves optimistically into every cached task
          list, so the chip behind the card updates too. */}
      <div className="mt-3 space-y-1 border-t border-gray-200 pt-3">
        {/* The editors are icon-only triggers; the words next to them are the
            current value, like the detail page's property chips. */}
        <PreviewRow label={t('taskPreview.status')}>
          <StatusEditor task={task} onOpenChange={pin} />
          <span className="truncate font-medium" style={{ color: task.statusColor }}>
            {task.statusLabel}
          </span>
        </PreviewRow>

        <PreviewRow label={t('taskPreview.priority')}>
          <PriorityEditor task={task} onOpenChange={pin} />
          <span
            className="truncate font-medium"
            style={{ color: task.priority === 'NONE' ? undefined : PRIORITY_COLOR[task.priority] }}
          >
            {PRIORITY_LABEL[task.priority]}
          </span>
        </PreviewRow>

        <PreviewRow label={t('taskPreview.assignee')}>
          <AssigneeEditor task={task} onOpenChange={pin} />
          <span className="truncate">{task.assignee?.name ?? t('taskPreview.unassigned')}</span>
        </PreviewRow>

        <PreviewRow label={t('taskPreview.due')}>
          <DueDateEditor task={task} onOpenChange={pin} alwaysVisible />
        </PreviewRow>

        <PreviewRow label={t('taskPreview.estimate')}>
          <EstimateEditor task={task} />
          {task.spentHours != null && task.spentHours > 0 && (
            <span className="truncate text-gray-500">
              · {t('taskPreview.logged', { spent: formatEstimate(task.spentHours) })}
            </span>
          )}
        </PreviewRow>

        {task.labels.length > 0 && (
          <PreviewRow label={t('taskPreview.labels')}>
            <span className="flex min-w-0 flex-wrap gap-1">
              {task.labels.map((label) => (
                <span
                  key={label.id}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-1.5 py-px text-[11px] font-medium text-gray-700"
                >
                  <span
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                  {label.name}
                </span>
              ))}
            </span>
          </PreviewRow>
        )}
      </div>
    </div>
  );
}

/**
 * The hover mechanics on their own: `content` is what the card shows. Task
 * chips, meeting chips and reminder chips all open through this, so hovering
 * any calendar chip behaves the same whatever species it is.
 */
export function HoverCard({ content, children }: { content: ReactNode; children: ReactNode }) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  // A state ref (not useRef) so the position effect re-runs once the card is in
  // the DOM and its real height is known — layout effects run before paint, so
  // the corrective pass is invisible.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // How many editor dropdowns inside the card are open. While > 0 the card
  // ignores mouse-leave and scroll, since the pointer is in another portal.
  const pinnedCount = useRef(0);
  const hovering = useRef(false);
  const suppressed = useContext(HoverCardSuppressedContext);

  function schedule(next: boolean) {
    if (next && suppressed) return;
    if (!next && pinnedCount.current > 0) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), next ? OPEN_DELAY_MS : CLOSE_DELAY_MS);
  }

  function setPinned(pinned: boolean) {
    pinnedCount.current = Math.max(0, pinnedCount.current + (pinned ? 1 : -1));
    // The dropdown just closed with the pointer somewhere else: let the card go.
    if (pinnedCount.current === 0 && !hovering.current) schedule(false);
  }

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (!suppressed) return;
    window.clearTimeout(timer.current);
    setOpen(false);
  }, [suppressed]);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const height = panel?.offsetHeight ?? 260;

    // Calendar cells are narrow, so the card goes beside the chip rather than
    // under it: to the right when there is room, otherwise to the left, and
    // only then clamped into the viewport.
    let left = rect.right + GAP;
    if (left + CARD_WIDTH > window.innerWidth - GAP) left = rect.left - CARD_WIDTH - GAP;
    left = Math.max(GAP, Math.min(left, window.innerWidth - CARD_WIDTH - GAP));

    // Vertically centred on the chip, then pulled inside the viewport.
    let top = rect.top + rect.height / 2 - height / 2;
    top = Math.max(GAP, Math.min(top, window.innerHeight - height - GAP));

    setPos({ top, left });
  }, [open, panel]);

  // A scroll would leave the card stranded next to nothing — unless it is a
  // scroll inside an open dropdown (the assignee list), which must not close it.
  useEffect(() => {
    if (!open) return;
    const close = () => {
      if (pinnedCount.current === 0) setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  return (
    <div
      ref={anchorRef}
      onMouseEnter={() => {
        hovering.current = true;
        schedule(true);
      }}
      onMouseLeave={() => {
        hovering.current = false;
        schedule(false);
      }}
    >
      {children}
      {open &&
        pos &&
        createPortal(
          // z-[59]: just under the editors' Popover (z-[60]) so their dropdowns
          // open above the card, still above the page chrome.
          <div
            ref={setPanel}
            className="ui-popover-panel pointer-events-auto fixed z-[59]"
            style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
            onMouseEnter={() => {
              hovering.current = true;
              window.clearTimeout(timer.current);
            }}
            onMouseLeave={() => {
              hovering.current = false;
              schedule(false);
            }}
          >
            <HoverCardPinContext.Provider value={setPinned}>{content}</HoverCardPinContext.Provider>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function TaskHoverCard({ task, children }: { task: Task; children: ReactNode }) {
  return <HoverCard content={<TaskPreviewCard task={task} />}>{children}</HoverCard>;
}
